import { useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  format,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import type { Task } from "../types";
import { getActivityTypeConfig } from "./activityTypes";
import { syncTaskWithGoogleCalendar } from "../providers/google/googleIntegrationService";
import { cn } from "@/lib/utils";

interface AgendaMonthViewProps {
  currentDate: Date;
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onSelectDate: (date: Date) => void;
  onRefresh?: () => void;
}

const WEEK_DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export const AgendaMonthView = ({
  currentDate,
  tasks,
  onEditTask,
  onSelectDate,
  onRefresh,
}: AgendaMonthViewProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | number | null>(
    null,
  );

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const handleDropOnDay = async (targetDay: Date, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverDay(null);
    setDraggedTaskId(null);

    const rawData = e.dataTransfer.getData("application/json");
    let taskId: string | number | null = null;
    let durationMinutes = 60;

    if (rawData) {
      try {
        const parsed = JSON.parse(rawData);
        taskId = parsed.taskId;
        if (parsed.durationMinutes) durationMinutes = parsed.durationMinutes;
      } catch {}
    }
    if (!taskId) {
      taskId = e.dataTransfer.getData("text/plain");
    }
    if (!taskId) return;

    const task = tasks.find((t) => String(t.id) === String(taskId));
    if (!task) return;

    const newStart = new Date(targetDay);
    if (task.data_inicio || task.due_date) {
      try {
        const orig = parseISO(task.data_inicio || task.due_date!);
        newStart.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
      } catch {
        newStart.setHours(9, 0, 0, 0);
      }
    } else {
      newStart.setHours(9, 0, 0, 0);
    }

    if (task.data_inicio && task.data_fim) {
      try {
        const origStart = parseISO(task.data_inicio);
        const origEnd = parseISO(task.data_fim);
        const diff = Math.round(
          (origEnd.getTime() - origStart.getTime()) / 60000,
        );
        if (diff > 0) durationMinutes = diff;
      } catch {}
    }

    const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);

    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: {
          ...task,
          data_inicio: newStart.toISOString(),
          due_date: newStart.toISOString(),
          data_fim: newEnd.toISOString(),
        },
        previousData: task,
      });

      // Sincroniza alteração de dia/horário com o Google Agenda
      syncTaskWithGoogleCalendar({
        id: task.id,
        text: task.text,
        type: task.type,
        data_inicio: newStart.toISOString(),
        due_date: newStart.toISOString(),
        data_fim: newEnd.toISOString(),
        link_chamada: task.link_chamada,
      }).catch(() => {});

      notify(
        `Atividade movida para ${format(newStart, "d 'de' MMMM", { locale: ptBR })} às ${format(newStart, "HH:mm")}`,
        { type: "success" },
      );
      onRefresh?.();
    } catch {
      notify("Erro ao mover atividade", { type: "error" });
    }
  };

  return (
    <div className="flex flex-col rounded-2xl border border-border/80 bg-card/60 overflow-hidden shadow-xs">
      {/* Header dos dias da semana */}
      <div className="grid grid-cols-7 border-b border-border/60 bg-muted/40 text-center py-2.5">
        {WEEK_DAYS.map((dayName) => (
          <div
            key={dayName}
            className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Grade de dias do mês */}
      <div className="grid grid-cols-7 divide-x divide-y divide-border/50">
        {calendarDays.map((day) => {
          const isCurrentMonth = isSameMonth(day, monthStart);
          const today = isToday(day);

          // Atividades do dia
          const dayTasks = tasks.filter((task) => {
            const taskDateStr = task.data_inicio || task.due_date;
            if (!taskDateStr) return false;
            try {
              return isSameDay(parseISO(taskDateStr), day);
            } catch {
              return false;
            }
          });

          const visibleTasks = dayTasks.slice(0, 3);
          const extraCount = dayTasks.length - 3;

          const isDragOver = dragOverDay === day.toISOString();

          return (
            <div
              key={day.toISOString()}
              onClick={() => onSelectDate(day)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDragOverDay(day.toISOString());
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setDragOverDay(null);
              }}
              onDrop={(e) => handleDropOnDay(day, e)}
              className={cn(
                "group relative min-h-[110px] md:min-h-[130px] p-2 transition-all flex flex-col cursor-pointer select-none hover:bg-muted/30",
                !isCurrentMonth && "bg-muted/10 opacity-40 hover:opacity-75",
                today && "bg-primary/[0.04] ring-1 ring-inset ring-primary/30",
                isDragOver &&
                  "ring-2 ring-primary border-primary bg-primary/20 scale-[1.02] shadow-md z-20",
              )}
            >
              {/* Header do dia: Número + Botão + */}
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                    today
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : isCurrentMonth
                        ? "text-foreground"
                        : "text-muted-foreground",
                  )}
                >
                  {format(day, "d")}
                </span>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectDate(day);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                  title="Agendar neste dia"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>

              {/* Lista compacta de eventos no dia */}
              <div className="flex-1 space-y-1 overflow-hidden">
                {isDragOver && (
                  <div className="text-[10px] font-bold text-primary bg-primary/20 rounded px-1.5 py-1 text-center border border-dashed border-primary animate-pulse">
                    Soltar aqui
                  </div>
                )}
                {visibleTasks.map((task) => {
                  const typeConfig = getActivityTypeConfig(task.type);
                  const Icon = typeConfig.icon;
                  const isDone = !!task.done_date;

                  let timeStr = "";
                  if (task.data_inicio || task.due_date) {
                    try {
                      timeStr = format(
                        parseISO(task.data_inicio || task.due_date!),
                        "HH:mm",
                      );
                    } catch {}
                  }

                  return (
                    <div
                      key={task.id}
                      draggable={true}
                      onDragStart={(e) => {
                        e.stopPropagation();
                        setDraggedTaskId(task.id);
                        e.dataTransfer.setData(
                          "application/json",
                          JSON.stringify({ taskId: task.id }),
                        );
                        e.dataTransfer.setData("text/plain", String(task.id));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDraggedTaskId(null);
                        setDragOverDay(null);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (draggedTaskId !== null) return;
                        onEditTask(task);
                      }}
                      className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border border-border/60 transition-all hover:scale-[1.02] truncate cursor-grab active:cursor-grabbing shadow-2xs select-none",
                        typeConfig.colorClass,
                        isDone && "line-through opacity-50",
                        draggedTaskId === task.id &&
                          "opacity-30 scale-95 ring-1 ring-primary",
                      )}
                      title={`${timeStr ? `${timeStr} - ` : ""}${task.text}`}
                    >
                      <Icon className="w-2.5 h-2.5 shrink-0" />
                      {timeStr && (
                        <span className="font-mono text-[9px] opacity-80 shrink-0">
                          {timeStr}
                        </span>
                      )}
                      <span className="truncate">{task.text}</span>
                    </div>
                  );
                })}

                {extraCount > 0 && (
                  <span className="block text-[10px] font-semibold text-primary/80 pl-1 hover:underline">
                    +{extraCount} mais
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
